import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SupplierSelect } from "../SupplierSelect";
import { Download, FileSpreadsheet, FileText, Loader2, Database } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

async function blobError(e) {
  try { return JSON.parse(await e.response.data.text()).detail; } catch { return "Export failed. Please try again."; }
}

function ModulePicker({ modules, selected, setSelected }) {
  const all = selected.length === modules.length;
  const toggle = (k) => setSelected(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label className="text-sm font-medium">Modules</Label>
        <button type="button" className="text-xs font-medium text-blue-600 hover:underline" data-testid="export-select-all-modules"
          onClick={() => setSelected(all ? [] : modules.map((m) => m.key))}>{all ? "Clear all" : "Select all"}</button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {modules.map((m) => (
          <label key={m.key} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${selected.includes(m.key) ? "border-blue-500 bg-blue-50 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <Checkbox checked={selected.includes(m.key)} onCheckedChange={() => toggle(m.key)} data-testid={`export-module-${m.key}`} />
            {m.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function FormatPicker({ format, setFormat }) {
  const opts = [["xlsx", "Excel (.xlsx)", FileSpreadsheet], ["csv", "CSV (.csv)", FileText]];
  return (
    <div>
      <Label className="mb-2 block text-sm font-medium">Format</Label>
      <RadioGroup value={format} onValueChange={setFormat} className="flex gap-3">
        {opts.map(([v, label, Icon]) => (
          <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${format === v ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
            <RadioGroupItem value={v} data-testid={`export-format-${v}`} /><Icon className="h-4 w-4 text-slate-500" /> {label}
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

function Filters({ f, setF, statuses, showSupplier }) {
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2" data-testid="export-filters">
      <div><Label className="mb-1.5 block text-xs">From date</Label><Input type="date" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="export-date-from" /></div>
      <div><Label className="mb-1.5 block text-xs">To date</Label><Input type="date" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="export-date-to" /></div>
      <div>
        <Label className="mb-1.5 block text-xs">Status {statuses.length === 0 && <span className="text-slate-400">(select one module that has statuses)</span>}</Label>
        <Select value={f.status || "all"} onValueChange={(v) => set("status")(v === "all" ? "" : v)} disabled={statuses.length === 0}>
          <SelectTrigger data-testid="export-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {showSupplier && <div><Label className="mb-1.5 block text-xs">Supplier</Label><SupplierSelect value={f.supplier_id} onChange={set("supplier_id")} placeholder="All suppliers" testId="export-supplier-filter" /></div>}
    </div>
  );
}

export const DataExportCenter = () => {
  const [modules, setModules] = useState(null);
  const [selected, setSelected] = useState([]);
  const [format, setFormat] = useState("xlsx");
  const [scope, setScope] = useState("all");
  const [f, setF] = useState({ date_from: "", date_to: "", status: "", supplier_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/export/modules").then(({ data }) => setModules(data)).catch(() => setModules([])); }, []);

  const chosen = useMemo(() => (modules || []).filter((m) => selected.includes(m.key)), [modules, selected]);
  const statuses = chosen.length === 1 ? chosen[0].statuses : [];
  useEffect(() => { if (!statuses.includes(f.status)) setF((p) => ({ ...p, status: "" })); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!modules || modules.length === 0) return null;

  const download = async () => {
    if (!selected.length) { toast.error("Select at least one module"); return; }
    setBusy(true);
    try {
      const body = { modules: selected, format, scope, ...(scope === "filtered" ? f : {}) };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const res = await api.post("/admin/export", body, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `IDEA_export.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(await blobError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="data-export-center">
      <h3 className="mb-1 flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-slate-900"><Database className="h-4 w-4 text-blue-600" /> Data Export Center</h3>
      <p className="mb-5 text-sm text-slate-500">Download live records as Excel or CSV. Export only — no records are changed. Currencies are always kept separate.</p>
      <div className="space-y-5">
        <ModulePicker modules={modules} selected={selected} setSelected={setSelected} />
        <FormatPicker format={format} setFormat={setFormat} />
        <div>
          <Label className="mb-2 block text-sm font-medium">Records</Label>
          <RadioGroup value={scope} onValueChange={setScope} className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="all" data-testid="export-scope-all" /> All records</label>
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="filtered" data-testid="export-scope-filtered" /> Filtered records</label>
          </RadioGroup>
        </div>
        {scope === "filtered" && <Filters f={f} setF={setF} statuses={statuses} showSupplier={chosen.some((m) => m.has_supplier)} />}
        <Button onClick={download} disabled={busy || !selected.length} data-testid="export-download-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {busy ? "Preparing..." : "Download Export"}
        </Button>
      </div>
    </div>
  );
};
